import { PaginatedPayload, WorkflowStatus, PaginationInfo, PaginatedResult } from './../../models/util.interface';
import { AppSettings } from '@/config/app-settings';
import { Job } from './../../models/job.interface';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import axios from 'axios';

@Module
export default class JobStore extends VuexModule {
  /* tslint:disable */
  private _jobs: Array<Job> = new Array();
  private _loading: boolean = false;
  private _selectedJob: Job | null = null;
  private _page: number = 1;
  private _paginationInfo: PaginationInfo = { limit: 10, pages: 0, page: '0', total: 0 };
  private _addSuccess: boolean = false;
  private _addError: string | null = null;
  private _updateSuccess: boolean = false;
  private _updateError: string | null = null;
  /* tslint:enable */

  @Mutation public setJobsMut(result: PaginatedResult<Job[]>) {
    this._jobs = result.data;
    this._paginationInfo = { limit: result.limit, pages: result.pages, page: result.page, total: result.total };
  }

  @Mutation public selectJob(job: Job) {
    this._selectedJob = job;
  }

  @Mutation public setJobsLoading(loading: boolean) {
    this._loading = loading;
  }

  @Mutation public addJobMut(job: Job) {
    if (job) {
      this._addSuccess = true;
      this._addError = null;
      this._jobs.push(job);
    } else {
      this._addSuccess = false;
      this._addError = this._addError ? this._addError : AppSettings.DEFAULT_ERROR;
    }
  }

  @Mutation public updateJobMut(job: Job) {
    if (job) {
      this._updateSuccess = true;
      this._updateError = null;
      this._jobs[this._jobs.findIndex((j: Job) => j._id === job._id)] = job;
    } else {
      this._updateSuccess = false;
      this._updateError = this._updateError ? this._updateError : AppSettings.DEFAULT_ERROR;
    }
  }

  @Mutation public removeJobMut(id: string) {
    if (id) {
      this._jobs = this._jobs.filter((job: Job) => job._id !== id);
    }
  }

  @Mutation public updatePageJobMut(page: number) {
    this._page = page;
  }

  @Mutation public resetJobWorkflowStatus(): void {
    this._addSuccess = false;
    this._addError = null;
    this._updateSuccess = false;
    this._updateError = null;
  }

  @Action({ commit: 'setJobsMut' }) public async getJobsAction(
    payload?: PaginatedPayload,
  ): Promise<PaginatedResult<Job[]>> {
    try {
      this.context.commit('setJobsLoading', true);
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/job`, {
        params: payload ? payload : { page: 1 },
      });
      this.context.commit('setJobsLoading', false);
      if (response.data.docs) {
        return {
          data: response.data.docs,
          limit: response.data.limit,
          pages: response.data.pages,
          page: response.data.page,
          total: response.data.total,
        };
      } else {
        return { data: [] };
      }
    } catch (err) {
      this.context.commit('setJobsLoading', false);
      return { data: [] };
    }
  }

  @Action({ commit: 'selectJob' }) public async getJobAction(id: string): Promise<Job | null> {
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/job/${id}`);
      return response.data;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'addJobMut' }) public async addJobAction(job: Job): Promise<Job | null> {
    try {
      this.context.commit('resetJobWorkflowStatus');
      await axios.post(`${process.env.VUE_APP_API_URL}/job`, { ...job });
      return job;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'updateJobMut' }) public async updateJobAction(job: Job): Promise<Job | null> {
    try {
      this.context.commit('resetJobWorkflowStatus');
      await axios.put(`${process.env.VUE_APP_API_URL}/job/${job._id}`, { ...job });
      return job;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'selectJob' }) public async selectJobAction(job: Job): Promise<Job | null> {
    return job;
  }

  @Action({ commit: 'removeJobMut' }) public async removeJobAction(id: string) {
    try {
      await axios.delete(`${process.env.VUE_APP_API_URL}/job/${id}`);
      return id;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'updatePageJobMut' }) public async changeJobPageAction(page: number): Promise<number | null> {
    return page;
  }

  @Action({ commit: 'resetJobWorkflowStatus' }) public resetJobWorkflowAction(): void {
    return;
  }

  get jobs(): Job[] {
    return this._jobs;
  }

  get jobsLoading(): boolean {
    return this._loading;
  }

  get selectedJob(): Job | null {
    return this._selectedJob;
  }

  get jobPage(): number {
    return this._page;
  }

  get jobPaginationInfo(): PaginationInfo {
    return this._paginationInfo;
  }

  get jobWorkflowStatus(): WorkflowStatus {
    return {
      add: {
        success: this._addSuccess,
        error: this._addError,
      },
      update: {
        success: this._updateSuccess,
        error: this._updateError,
      },
    };
  }
}
